// Metricas diarias (docs/PLAN-CIERRE-DIARIO.md): lo que el cierre nocturno
// deja en metricas_diarias y GET /sites/:siteId/metrics expone. Son
// agregados SIN PII: 30 filas de esta lectura arman el informe de servicio
// mensual. `.nullable()` aqui significa "no calculable ese dia" (sin trips,
// sin demoras, sin ruteo): un dato distinto de cero, no un dato ausente.
import { z } from "zod";
import { Decimal, Integer, CalendarDate, Instant } from "./common.js";
export const CountByType = z
    .object({
    type: z.string().min(1).openapi({
        description: "Clave del desglose: un VehicleType en tripsByType, un ExceptionType en exceptionsByType.",
        example: "shuttle",
    }),
    count: Integer.min(0),
})
    .openapi("CountByType", { description: "Un par (tipo, cantidad) de un desglose." });
export const DailyMetric = z
    .object({
    date: CalendarDate,
    providerId: z.string().min(1).openapi({
        description: "Proveedor de la fila, o '*' para el agregado de toda la planta.",
        example: "*",
    }),
    tripsOperated: Integer.min(0).openapi({ description: "Viajes completados del dia." }),
    tripsByType: z.array(CountByType).openapi({
        description: "Viajes completados del dia, desglosados por tipo de vehiculo.",
    }),
    averageOccupancyPct: Decimal.min(0).max(100).nullable().openapi({
        description: "Ocupacion promedio en porcentaje; null si no hubo viajes con capacidad conocida.",
    }),
    idleSeats: Integer.min(0),
    plannedKm: Decimal.min(0).nullable().openapi({
        description: "Kilometros planificados; null si no hubo ruteo.",
    }),
    actualKm: Decimal.min(0).nullable().openapi({
        description: "Kilometros reales por telemetria; null sin posiciones.",
    }),
    deviationPct: Decimal.nullable().openapi({
        description: "Desvio de km reales sobre planificados en porcentaje; null si falta alguno.",
    }),
    handoverOnTimePct: Decimal.min(0).max(100).nullable().openapi({
        description: "Porcentaje de viajes que llegaron a tiempo al relevo; null sin viajes de ingreso.",
    }),
    // en castellano: trayecto, puerta a puerta del pasajero (GLOSSARY §11).
    journeyOver45Count: Integer.min(0).openapi({
        description: "Pasajeros cuyo trayecto supero el tope de la politica.",
    }),
    journeyOver45P95Min: Decimal.min(0).nullable().openapi({
        description: "P95 en minutos de los trayectos que superaron el tope; null si no hubo excesos.",
    }),
    delaysTotal: Integer.min(0),
    delaysNotifiedOnTime: Integer.min(0),
    delaysNotifiedLate: Integer.min(0),
    delaysNotNotified: Integer.min(0),
    noShows: Integer.min(0),
    duplicateRequests: Integer.min(0),
    exceptionsByType: z.array(CountByType).openapi({
        description: "Excepciones operativas del dia, desglosadas por tipo.",
    }),
    meanResolutionTimeMin: Decimal.min(0).nullable().openapi({
        description: "Minutos promedio entre deteccion y resolucion; null sin excepciones resueltas. Las vencidas no entran en el promedio.",
    }),
    // Ambos son opcionales porque las filas anteriores a la migracion 0015
    // no los tienen; calculationVersion >= 4 los garantiza.
    exceptionsExpired: Integer.min(0).optional().openapi({
        description: "Excepciones que el cierre diario vencio (en castellano: vencidas) porque quedaron abiertas. Ausente en filas calculadas antes de calculationVersion 4.",
    }),
    tripsUnclosed: Integer.min(0).optional().openapi({
        description: "Viajes del dia sin cierre operativo: sin telemetria de llegada, cancelados por el barrido del cierre. Ausente en filas calculadas antes de calculationVersion 4.",
    }),
    calculationVersion: Integer.positive().openapi({
        description: "Version del algoritmo de cierre que produjo la fila.",
    }),
    computedAt: Instant,
})
    .openapi("DailyMetric", {
    description: "Fila de metricas de un dia y un proveedor ('*' = toda la planta). Agregados sin PII.",
});
export const RangeQuery = z
    .object({
    from: CalendarDate.optional().openapi({ description: "Inicio del rango, inclusive." }),
    to: CalendarDate.optional().openapi({ description: "Fin del rango, inclusive." }),
})
    .openapi("RangeQuery", {
    description: "Rango [from, to] inclusivo por fecha operativa. La ruta rechaza from > to con 400.",
});
export const ListMetricsResponse = z
    .object({ metrics: z.array(DailyMetric) })
    .openapi("ListMetricsResponse", {
    description: "Metricas diarias de la planta, ordenadas por fecha y proveedor.",
});
//# sourceMappingURL=metrics.js.map